import { create } from 'zustand'
import { type StoreApi } from 'zustand/esm/vanilla'
import type { IconTypes } from '@components/utils/Icon'

export type AlertType = {
  type: 'success' | 'info' | 'warning' | 'error'
  details: {
    title: string
    description?: string
  }
  icon?: IconTypes
}

export type AlertsPathType = string

type AlertsByPath = Record<AlertsPathType, AlertType[] | undefined | null>

type StoreState = {
  alerts: AlertsByPath
}

type SetterType = StoreApi<StoreState & StoreActions>['setState']

type StoreActions = {
  setAlerts: (options: { alertsPath: string; alerts: AlertType[] }) => void
  clearAlerts: (alertsPath: AlertsPathType) => void
  clearAlert: (alertsPath: AlertsPathType, indexToRemove: number) => void
}

function clearAlert(set: SetterType, alertsPath: AlertsPathType, indexToRemove: number) {
  set(({ alerts }) => {
    const alertsByPath = alerts[alertsPath] ?? []
    // Mutates the array by removing the element at the specified index
    alertsByPath.splice(indexToRemove, 1)
    return { alerts: { ...alerts, [alertsPath]: alertsByPath } }
  })
}

function clearAlerts(set: SetterType, alertsPath: AlertsPathType) {
  set(({ alerts }) => {
    delete alerts[alertsPath]
    return { alerts }
  })
}

function setAlerts(set: SetterType, alertsPath: AlertsPathType, newAlerts: AlertType[]) {
  set(({ alerts }) => {
    const matchingAlerts = alerts[alertsPath] ?? []

    return {
      alerts: {
        ...alerts,
        [alertsPath]: [...matchingAlerts, ...newAlerts],
      },
    }
  })
}

export const useAlertsStore = create<StoreState & StoreActions>()((set) => ({
  alerts: {} satisfies AlertsByPath,
  setAlerts: ({ alertsPath, alerts }) => {
    setAlerts(set, alertsPath, alerts)
  },
  clearAlerts: (alertsPath) => {
    clearAlerts(set, alertsPath)
  },
  clearAlert: (alertsPath, indexToRemove) => {
    clearAlert(set, alertsPath, indexToRemove)
  },
}))
